// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

// i18next setup. Resources are bundled (no async backend) so t() is ready on the
// first render — important because the login/unlock screens render before the
// encrypted event log is available. ICU MessageFormat provides locale-correct
// plurals and interpolation ({count, plural, ...}); numbers/dates/currency are
// formatted separately via hooks/useFormat.ts and lib/currency-math.ts.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import ICU from 'i18next-icu';
import { DEFAULT_LOCALE, SUPPORTED_LOCALE_CODES } from './locales.js';
import { resolveInitialLanguage, applyDocumentLocale } from './language.js';
import { setDisplayLocale } from '../lib/formatLocale.js';
import en from './locales/en.json';

// Eagerly load every locale bundle. Dropping `locales/<code>.json` makes it show
// up here automatically — no code change needed (see locales/README.md). Review
// sidecars live in `locales/review/` and are intentionally NOT matched here.
const modules = import.meta.glob('./locales/*.json', { eager: true }) as Record<
  string,
  { default: Record<string, Record<string, unknown>> }
>;

const resources: Record<string, Record<string, Record<string, unknown>>> = {};
for (const [path, mod] of Object.entries(modules)) {
  const code = path.replace(/^.*\/locales\//, '').replace(/\.json$/, '');
  if (SUPPORTED_LOCALE_CODES.includes(code)) {
    resources[code] = mod.default;
  }
}

// Namespaces are the top-level keys of the English source of truth.
export const NAMESPACES = Object.keys(en);
export const DEFAULT_NS = 'common';

const initialLng = resolveInitialLanguage();
applyDocumentLocale(initialLng);
setDisplayLocale(initialLng);

void i18n
  .use(ICU)
  .use(initReactI18next)
  .init({
    resources,
    lng: initialLng,
    fallbackLng: DEFAULT_LOCALE,
    ns: NAMESPACES,
    defaultNS: DEFAULT_NS,
    nsSeparator: ':',
    keySeparator: '.',
    returnNull: false,
    interpolation: {
      // React already escapes; ICU handles its own argument substitution.
      escapeValue: false,
    },
    // Resources are bundled and init is synchronous, so t() is ready on first
    // render — no Suspense boundary needed (and none exists around the shell).
    react: { useSuspense: false },
  });

// Keep the document's lang + font-script attributes and the display-locale used
// by the formatters in sync with the active language.
i18n.on('languageChanged', (lng) => {
  applyDocumentLocale(lng);
  setDisplayLocale(lng);
});

export default i18n;
